import React, { useState, useEffect } from 'react';
import { Paper, Box, Button, TextField, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tab } from '@material-ui/core';

const MAX_WORD_LENGTH = 9;

const WordsTab = ({ config, onSave, levelCount, wordsPerLevel }) => {
  const [selectedLevel, setSelectedLevel] = useState(1);
  const [words, setWords] = useState({});

  useEffect(() => {
    // Initialize words from config
    const initialWords = {};
    for (let level = 1; level <= levelCount; level += 1) {
      initialWords[level] = [];
      for (let order = 1; order <= wordsPerLevel; order += 1) {
        const existingWord = config?.words?.find((w) => w.level_number === level && w.display_order === order);
        initialWords[level].push(existingWord?.word || '');
      }
    }
    setWords(initialWords);
  }, [config, levelCount, wordsPerLevel]);

  const handleWordChange = (level, order, value) => {
    if (value.length > MAX_WORD_LENGTH) {
      return;
    }
    setWords((prev) => ({
      ...prev,
      [level]: prev[level].map((word, idx) => (idx === order - 1 ? value : word)),
    }));
  };

  const handleSave = () => {
    const allWords = [];
    for (let level = 1; level <= levelCount; level += 1) {
      for (let order = 1; order <= wordsPerLevel; order += 1) {
        const word = words[level]?.[order - 1] || '';
        if (word.trim()) {
          allWords.push({
            level_number: level,
            word: word.trim(),
            display_order: order,
          });
        }
      }
    }

    onSave({
      ...config,
      words: allWords,
    });
  };

  return (
    <Paper>
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          Game Words
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          15 words per level. Players need to find 5 words to complete a level.
        </Typography>

        <Box mt={2}>
          <Tabs
            value={selectedLevel - 1}
            onChange={(_, newValue) => setSelectedLevel(newValue + 1)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            {Array.from({ length: levelCount }, (_, i) => (
              <Tab key={i + 1} label={`Level ${i + 1}`} />
            ))}
          </Tabs>
        </Box>

        <Box mt={3}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={60}>#</TableCell>
                  <TableCell>Word</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: wordsPerLevel }, (_, i) => i + 1).map((order) => (
                  <TableRow key={order}>
                    <TableCell>{order}</TableCell>
                    <TableCell>
                      <TextField
                        value={words[selectedLevel]?.[order - 1] || ''}
                        onChange={(e) => handleWordChange(selectedLevel, order, e.target.value)}
                        fullWidth
                        size="small"
                        placeholder={`Word ${order}`}
                        variant="outlined"
                        error={words[selectedLevel]?.[order - 1]?.length > MAX_WORD_LENGTH}
                        helperText={
                          words[selectedLevel]?.[order - 1]?.length > MAX_WORD_LENGTH
                            ? `Max ${MAX_WORD_LENGTH} characters`
                            : ''
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="textSecondary">
            Level {selectedLevel}: {words[selectedLevel]?.filter((w) => w.trim()).length || 0} / {wordsPerLevel} words
            filled
          </Typography>
          <Button variant="contained" color="primary" onClick={handleSave}>
            Save All Words
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default WordsTab;
